// Automatic file grouping by similar names
import { AudioFile, Folder } from '@types';

/**
 * Detects version type from file name
 * Returns the detected version type or undefined
 */
export function detectVersionType(fileName: string): 'tagged' | 'clean' | 'wav' | 'mp3' | 'stems' | 'master' | undefined {
  const lowerName = fileName.toLowerCase();
  
  // Check for version indicators in order of specificity
  if (lowerName.includes('master')) return 'master';
  if (lowerName.includes('stem')) return 'stems';
  if (lowerName.includes('tagged') || lowerName.includes('tag')) return 'tagged';
  if (lowerName.includes('clean') || lowerName.includes('untagged')) return 'clean';
  
  // Check for ZIP files that contain stems (common patterns: "Track Name - Stems.zip", "Track Name Stems.zip")
  if (lowerName.endsWith('.zip')) {
    // If ZIP contains "stem" in name, it's likely a stems archive
    if (lowerName.includes('stem')) return 'stems';
    // If ZIP is in a folder or has a name suggesting it's stems, treat as stems
    // This is a heuristic - ZIP files are often used for stems
    return 'stems';
  }
  
  // Check file extension
  if (lowerName.endsWith('.wav')) return 'wav';
  if (lowerName.endsWith('.mp3')) return 'mp3';
  
  return undefined;
}

/**
 * Extracts the base name from a file name by removing common version indicators
 * e.g., "Summer Vibes - Tagged.wav" -> "summer vibes"
 * e.g., "Summer Vibes - Stems.zip" -> "summer vibes"
 * e.g., "Summer Vibes Stems" (folder) -> "summer vibes"
 */
const VERSION_SUFFIX_PATTERNS = [
  /\s*-\s*(tagged|clean|untagged|wav|mp3|master|stems|final|mix)\s*$/i,
  /\s*\((tagged|clean|untagged|wav|mp3|master|stems|final|mix)\)\s*$/i,
  /\s*_(tagged|clean|untagged|wav|mp3|master|stems|final|mix)\s*$/i,
  /\s*(tagged|clean|untagged|wav|mp3|master|stems|final|mix)\s*$/i,
];

function stripVersionSuffixFromFileName(fileName: string): string {
  let baseName = fileName.replace(/\.[^/.]+$/, '');
  VERSION_SUFFIX_PATTERNS.forEach((pattern) => {
    baseName = baseName.replace(pattern, '');
  });
  return baseName.trim().replace(/\s+/g, ' ');
}

/**
 * Display name for UI – preserves original casing from the file name.
 */
export function getDisplayBaseName(fileName: string): string {
  return stripVersionSuffixFromFileName(fileName);
}

export function getBaseName(fileName: string): string {
  return stripVersionSuffixFromFileName(fileName).toLowerCase();
}

/**
 * Checks if a file name suggests it contains stems (ZIP or folder)
 */
export function isStemsArchive(fileName: string): boolean {
  const lowerName = fileName.toLowerCase();
  // Check if it's a ZIP file or folder name that suggests stems
  return (
    lowerName.endsWith('.zip') && lowerName.includes('stem')
  ) || (
    !fileName.includes('.') && lowerName.includes('stem')
  );
}

/**
 * Calculates similarity between two strings (0-1)
 * Uses multiple strategies for better matching:
 * 1. Exact match after normalization
 * 2. Prefix matching (if one is a prefix of the other)
 * 3. Levenshtein distance for fuzzy matching
 */
export function getSimilarity(str1: string, str2: string): number {
  // Normalize both strings
  const normalized1 = str1.toLowerCase().trim();
  const normalized2 = str2.toLowerCase().trim();
  
  // Exact match after normalization
  if (normalized1 === normalized2) return 1.0;
  
  // Empty strings
  if (normalized1.length === 0 || normalized2.length === 0) return 0.0;
  
  // Check if one is a prefix of the other (common case: "Track Name" vs "Track Name - Tagged")
  const shorter = normalized1.length < normalized2.length ? normalized1 : normalized2;
  const longer = normalized1.length >= normalized2.length ? normalized1 : normalized2;
  
  if (longer.startsWith(shorter)) {
    // If shorter is at least 70% of longer, consider it a good match
    const prefixRatio = shorter.length / longer.length;
    if (prefixRatio >= 0.7) {
      return 0.85 + (prefixRatio - 0.7) * 0.5; // Scale between 0.85 and 1.0
    }
  }
  
  // Check for common words (token-based similarity)
  const words1 = normalized1.split(/\s+/).filter(w => w.length > 2);
  const words2 = normalized2.split(/\s+/).filter(w => w.length > 2);
  
  let wordSimilarity = 0;
  if (words1.length > 0 && words2.length > 0) {
    const commonWords = words1.filter(w => words2.includes(w));
    const totalWords = Math.max(words1.length, words2.length);
    wordSimilarity = commonWords.length / totalWords;
    
    // If most words match, it's likely the same track
    if (wordSimilarity >= 0.6) {
      return 0.75 + wordSimilarity * 0.25; // Scale between 0.75 and 1.0
    }
  }
  
  // Calculate Levenshtein distance for fuzzy matching
  const editDistance = levenshteinDistance(longer, shorter);
  const levenshteinSimilarity = (longer.length - editDistance) / longer.length;
  
  // Combine word similarity with Levenshtein if we have word data
  if (words1.length > 0 && words2.length > 0 && wordSimilarity > 0) {
    const combinedSimilarity = (wordSimilarity * 0.4) + (levenshteinSimilarity * 0.6);
    return Math.max(combinedSimilarity, levenshteinSimilarity);
  }
  
  return levenshteinSimilarity;
}

/**
 * Levenshtein distance algorithm
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

export interface FileGroup {
  id: string;
  baseName: string;
  mainFile: AudioFile;
  versions: AudioFile[];
  stemsFolders?: Folder[]; // Folders that contain stems for this track
}

/** Creation timestamp (createdAt, else uploadedAt) for sorting. */
export function getFileCreatedAtMs(file: AudioFile): number {
  for (const iso of [file.createdAt, file.uploadedAt]) {
    if (!iso) continue;
    const t = Date.parse(iso);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}

export function getGroupCreatedAtMs(group: FileGroup): number {
  return getFileCreatedAtMs(group.mainFile);
}

/** Newest groups first (by main file creation date). */
export function sortFileGroupsByCreatedAtDesc(groups: FileGroup[]): FileGroup[] {
  return [...groups].sort((a, b) => getGroupCreatedAtMs(b) - getGroupCreatedAtMs(a));
}

/**
 * Collaborator label for list/grid: main file first, else any version in the group.
 */
export function getCollaboratorLabelForGroup(group: FileGroup): string | undefined {
  const main = group.mainFile.collaborator?.trim();
  if (main) return main;
  for (const v of group.versions) {
    const c = v.collaborator?.trim();
    if (c) return c;
  }
  return undefined;
}

/**
 * Groups files by similar names automatically
 * Files with similarity > 0.8 are grouped together
 * Also auto-detects version types if not set
 * Priority: Manual groupId > Similar names
 */
export function groupFilesByName(files: AudioFile[]): FileGroup[] {
  const groups: FileGroup[] = [];
  const processedIds = new Set<string>();
  const groupIdsUsed = new Set<string>(); // Track used group IDs to prevent duplicates
  
  // Remove duplicates from input files (by ID) to prevent duplicate groups
  const uniqueFiles = new Map<string, AudioFile>();
  files.forEach(file => {
    if (!uniqueFiles.has(file.id)) {
      uniqueFiles.set(file.id, file);
    }
  });
  const deduplicatedFiles = Array.from(uniqueFiles.values());
  
  // Auto-detect version types for files without versionType
  const filesWithVersionTypes = deduplicatedFiles.map(file => {
    if (!file.versionType) {
      const detected = detectVersionType(file.name);
      if (detected) {
        console.log(`🔍 Auto-detected version type for "${file.name}": ${detected}`);
        return { ...file, versionType: detected };
      }
    }
    return file;
  });
  
  // First, group by manual groupId
  const manualGroups = new Map<string, AudioFile[]>();
  filesWithVersionTypes.forEach(file => {
    if (file.groupId) {
      console.log(`🔗 Adding to manual group: ${file.name} → groupId: ${file.groupId}, fileId: ${file.id}`);
      if (!manualGroups.has(file.groupId)) {
        manualGroups.set(file.groupId, []);
      }
      manualGroups.get(file.groupId)!.push(file);
      processedIds.add(file.id);
    }
  });
  
  console.log('✅ Processed IDs after manual grouping:', Array.from(processedIds));
  
  // Create groups from manual groupIds
  manualGroups.forEach((groupFiles, groupId) => {
    // Ensure group ID is unique
    let uniqueGroupId = groupId;
    let counter = 1;
    while (groupIdsUsed.has(uniqueGroupId)) {
      uniqueGroupId = `${groupId}-${counter}`;
      counter++;
    }
    groupIdsUsed.add(uniqueGroupId);
    
    const mainFile = selectMainFile(groupFiles);
    const versions = groupFiles.filter(f => f.id !== mainFile.id);
    
    console.log(`📦 Manual Group "${uniqueGroupId}": main=${mainFile.name}, versions=${versions.length}, versionNames=${versions.map(v => v.name).join(', ')}`);
    
    groups.push({
      id: uniqueGroupId,
      baseName: getBaseName(mainFile.name),
      mainFile,
      versions,
    });
  });
  
  // Then, auto-group remaining files by similar names
  // Use a more robust approach: build similarity graph and find connected components
  const unprocessedFiles = filesWithVersionTypes.filter(f => !processedIds.has(f.id));
  
  if (unprocessedFiles.length > 0) {
    // Build similarity graph
    const similarityGraph = new Map<string, Set<string>>();
    const fileBaseNames = new Map<string, string>();
    
    // Initialize graph nodes
    unprocessedFiles.forEach(file => {
      const baseName = getBaseName(file.name);
      fileBaseNames.set(file.id, baseName);
      similarityGraph.set(file.id, new Set());
    });
    
    // Build edges based on similarity
    for (let i = 0; i < unprocessedFiles.length; i++) {
      const file1 = unprocessedFiles[i];
      const baseName1 = fileBaseNames.get(file1.id)!;
      const isStemsFile1 = file1.versionType === 'stems' || isStemsArchive(file1.name);
      
      for (let j = i + 1; j < unprocessedFiles.length; j++) {
        const file2 = unprocessedFiles[j];
        const baseName2 = fileBaseNames.get(file2.id)!;
        const similarity = getSimilarity(baseName1, baseName2);
        
        // For stems archives, use a slightly lower threshold (70%) to catch more matches
        // For regular files, use the standard threshold (75% - lowered for better grouping)
        const threshold = isStemsFile1 ? 0.7 : 0.75;
        
        if (similarity > threshold) {
          similarityGraph.get(file1.id)!.add(file2.id);
          similarityGraph.get(file2.id)!.add(file1.id);
        }
      }
    }
    
    // Find connected components (transitive grouping)
    const visited = new Set<string>();
    const findConnectedComponent = (startId: string): AudioFile[] => {
      const component: AudioFile[] = [];
      const queue: string[] = [startId];
      visited.add(startId);
      
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        const file = unprocessedFiles.find(f => f.id === currentId);
        if (file) {
          component.push(file);
        }
        
        const neighbors = similarityGraph.get(currentId) || new Set();
        neighbors.forEach(neighborId => {
          if (!visited.has(neighborId)) {
            visited.add(neighborId);
            queue.push(neighborId);
          }
        });
      }
      
      return component;
    };
    
    // Process each connected component as a group
    unprocessedFiles.forEach(file => {
      if (visited.has(file.id)) return;
      
      const component = findConnectedComponent(file.id);
      if (component.length === 0) return;
      
      // If only one file, create a single-file group
      if (component.length === 1) {
        const singleFile = component[0];
        const baseName = fileBaseNames.get(singleFile.id)!;
        
        let uniqueGroupId = singleFile.id;
        let counter = 1;
        while (groupIdsUsed.has(uniqueGroupId)) {
          uniqueGroupId = `${singleFile.id}-${counter}`;
          counter++;
        }
        groupIdsUsed.add(uniqueGroupId);
        
        groups.push({
          id: uniqueGroupId,
          baseName,
          mainFile: singleFile,
          versions: [],
        });
        
        processedIds.add(singleFile.id);
        return;
      }
      
      // Multiple files: group them together
      const baseName = fileBaseNames.get(file.id)!;
      const isStemsFile = component.some(f => f.versionType === 'stems' || isStemsArchive(f.name));
      
      // Mark stems files
      component.forEach(f => {
        if (isStemsFile && f.versionType !== 'stems' && isStemsArchive(f.name)) {
          f.versionType = 'stems';
          console.log(`🎵 Marked "${f.name}" as stems version`);
        }
      });
      
      // Determine the "main" file
      const mainFile = selectMainFile(component);
      const versions = component.filter(f => f.id !== mainFile.id);
      
      // Ensure group ID is unique
      let uniqueGroupId = mainFile.id;
      let counter = 1;
      while (groupIdsUsed.has(uniqueGroupId)) {
        uniqueGroupId = `${mainFile.id}-${counter}`;
        counter++;
      }
      groupIdsUsed.add(uniqueGroupId);
      
      console.log(`📦 Auto Group "${baseName}": main=${mainFile.name}, versions=${versions.length}${isStemsFile ? ' (stems archive detected)' : ''}`);
      
      groups.push({
        id: uniqueGroupId,
        baseName,
        mainFile,
        versions,
      });
      
      // Mark all files in component as processed
      component.forEach(f => processedIds.add(f.id));
    });
  }
  
  return groups;
}

/**
 * Selects the "main" file from a group based on priority
 * Manual override (isMainVersion) has highest priority
 */
function selectMainFile(files: AudioFile[]): AudioFile {
  // 1. Check for manual override first
  const manualMain = files.find(f => f.isMainVersion === true);
  if (manualMain) {
    return manualMain;
  }

  // 2. Exclude tagged/processed versions from being auto-selected as main —
  //    they are always derivative works. Only fall back to them when there is
  //    no other candidate (e.g. a group that only contains tagged files).
  const DERIVED_VERSION_TYPES = new Set(['tagged', 'clean', 'stems']);
  const nonDerived = files.filter(f => !DERIVED_VERSION_TYPES.has(f.versionType ?? ''));
  const candidates = nonDerived.length > 0 ? nonDerived : files;

  // 3. Automatic selection based on priority
  const statusPriority: Record<string, number> = {
    'ready': 6,
    'untagged': 5,
    'tagged': 4,
    'master': 3,
    'draft': 2,
    'wip': 1,
    'archived': 0,
  };

  const versionPriority: Record<string, number> = {
    'master': 5,
    'wav': 4,
    'mp3': 2,
    'stems': 1,
  };

  return candidates.reduce((best, current) => {
    const bestStatusScore = statusPriority[best.status ?? ''] ?? 0;
    const currentStatusScore = statusPriority[current.status ?? ''] ?? 0;

    if (currentStatusScore > bestStatusScore) return current;
    if (currentStatusScore < bestStatusScore) return best;

    // Status equal — compare version type (files without a type score 3, between wav and mp3)
    const bestVersionScore = versionPriority[best.versionType ?? ''] ?? 3;
    const currentVersionScore = versionPriority[current.versionType ?? ''] ?? 3;

    if (currentVersionScore > bestVersionScore) return current;
    if (currentVersionScore < bestVersionScore) return best;

    return best;
  });
}

/**
 * Generate a color for a group based on its ID
 */
export function getGroupColor(groupId: string): string {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#14B8A6', // teal
    '#F97316', // orange
  ];
  
  // Simple hash function to get consistent color for same ID
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = groupId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Extended file group that can include folders with stems
 */
export interface ExtendedFileGroup extends FileGroup {
  stemsFolders?: Folder[]; // Folders that contain stems for this track
}

/**
 * Links folders with stems to their matching tracks
 * Returns a map of file group IDs to folders that contain stems for that track
 */
export function linkStemsFoldersToTracks(
  fileGroups: FileGroup[],
  folders: Folder[]
): Map<string, Folder[]> {
  const stemsFolderMap = new Map<string, Folder[]>();
  
  // Find folders that likely contain stems
  const stemsFolders = folders.filter(folder => {
    const lowerName = folder.name.toLowerCase();
    return lowerName.includes('stem');
  });
  
  // For each stems folder, try to find a matching track group
  stemsFolders.forEach(folder => {
    const folderBaseName = getBaseName(folder.name);
    let bestMatch: { group: FileGroup; similarity: number } | null = null;
    
    // Find the best matching track group
    fileGroups.forEach(group => {
      const similarity = getSimilarity(folderBaseName, group.baseName);
      
      // Use a threshold of 70% similarity for folder matching
      if (similarity > 0.7) {
        if (!bestMatch || similarity > bestMatch.similarity) {
          bestMatch = { group, similarity };
        }
      }
    });
    
    // If we found a match, add the folder to that group's stems folders
    if (bestMatch !== null) {
      const match: { group: FileGroup; similarity: number } = bestMatch;
      const groupId = match.group.id;
      if (!stemsFolderMap.has(groupId)) {
        stemsFolderMap.set(groupId, []);
      }
      stemsFolderMap.get(groupId)!.push(folder);
      console.log(`🔗 Linked stems folder "${folder.name}" to track group "${match.group.baseName}" (similarity: ${(match.similarity * 100).toFixed(1)}%)`);
    }
  });
  
  return stemsFolderMap;
}

